import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import type {AfriexBusiness, BusinessMetaFlags} from '@/types/AfriexBusiness';
import type {BusinessKYBDocumentType} from '@/types/BusinessKYBDocumentType';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import type {
  businessLoadingFlags,
  KYBUpdatePayload,
  PostFundingConfigPayload,
} from './types';

const useMerchantKYB = () => {
  const params = useParams();
  const businessId = params?.businessId || '';
  const [loading, setIsLoading] = useState(false);
  const [loadingKYB, setIsLoadingKYB] = useState(false);
  const [isPostFundingConfigSubmitting, setIsPostFundingConfigSubmitting] =
    useState(false);
  const [businessLoadingFlags, setBusinessLoadingFlags] =
    useState<businessLoadingFlags>({
      isApprovedForAPI: false,
      isVerified: false,
      isDeactivated: false,
      isApprovedForFeeWallet: false,
      isApprovedForPostFunding: false,
    });
  const [businessDetails, setBusinessDetails] = useState<AfriexBusiness>(
    {} as AfriexBusiness,
  );

  const fetchBusinessDetails = useCallback(async (): Promise<void> => {
    try {
      if (!businessId) {
        return;
      }
      setIsLoading(true);
      const response = await merchantService.getBusinessDetails(businessId);
      if (response) {
        setBusinessDetails(response.business);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      toast.error('Failed to fetch business details:' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  const updateBusinessKYB = useCallback(
    async (kyb: KYBUpdatePayload): Promise<void> => {
      try {
        if (!businessId) {
          return;
        }
        setIsLoadingKYB(true);
        await merchantService.updateBusinessKYB(businessId, kyb);
        toast.success('KYC Documents uploaded successfully');
        setTimeout(() => window.location.reload(), 1500);
      } catch (err) {
        const errorMessage = getErrorMessage(err, '');
        toast.error('Failed to upload KYC Documents:' + errorMessage);
      } finally {
        setIsLoadingKYB(false);
      }
    },
    [businessId],
  );

  const requestAdditionalDocuments = useCallback(
    async (documents: BusinessKYBDocumentType[]): Promise<void> => {
      if (!businessId) {
        throw new Error('Missing business id');
      }
      await merchantService.requestBusinessKYBDocuments(businessId, documents);
      setTimeout(() => window.location.reload(), 1500);
    },
    [businessId],
  );

  // Derived purely from `businessDetails` (itself set by fetchBusinessDetails
  // above), so this reads during render instead of round-tripping through an
  // effect + separate `fileUrls` state. `setIsLoading(false)` was dropped from
  // the old effect version: by the time `businessDetails` carries any keys,
  // fetchBusinessDetails's own `finally` has already cleared `loading` in the
  // same batched update, so the call was always a no-op.
  const fileUrls = useMemo(() => {
    try {
      if (!businessId || Object.keys(businessDetails).length < 1) {
        return [];
      }

      const kyb = businessDetails?.meta?.kyb;
      const docs = kyb?.docs ?? [];
      const data = kyb?.data ?? {};

      const docsUrls: {url: string; name: string}[] = [];
      for (const doc of docs) {
        const source = doc?.media?.source;
        // An empty source is skipped, as the original truthy filter did.
        if (typeof source === 'string' && source) {
          docsUrls.push({url: source, name: doc.media?.name || 'Document'});
        }
      }

      // `OTHERS` is not a core BusinessKYBDocumentType — cast so admin can
      // surface any legacy free-form entry that used to be written to that
      // key in older KYB uploads. Server never writes it today.
      const {
        PROOF_OF_BUSINESS_ADDRESS,
        ARTICLES_DOCUMENT,
        SHAREHOLDER_REGISTRY,
        BUSINESS_CERTIFICATE,
        OTHERS,
      } = data as typeof data & {OTHERS?: string};

      const allFiles = {
        BUSINESS_CERTIFICATE,
        PROOF_OF_BUSINESS_ADDRESS,
        ARTICLES_DOCUMENT,
        SHAREHOLDER_REGISTRY,
        OTHERS,
      };

      const dataDocsUrls = Object.entries(allFiles)
        .filter(
          (entry): entry is [string, string] =>
            typeof entry[1] === 'string' && entry[1].trim() !== '',
        )
        .map(([key, fileStr]) => ({
          url: fileStr,
          name: key.replace(/_/g, ' '),
        }));

      return [...docsUrls, ...dataDocsUrls];
    } catch (err) {
      console.error('Error fetching files:', err);
      return [];
    }
  }, [businessId, businessDetails]);

  const updateBusinessFlag = useCallback(
    async (flag?: Partial<BusinessMetaFlags>): Promise<void> => {
      if (!businessId || !flag) {
        return;
      }

      const keys = Object.keys(flag) as (keyof typeof businessLoadingFlags)[];
      const flagKey = keys[0];
      if (!flagKey) {
        return;
      }

      try {
        setBusinessLoadingFlags((prev) => ({...prev, [flagKey]: true}));
        // Post-funding approval has its own role-restricted endpoint.
        await (flag.isApprovedForPostFunding === undefined
          ? merchantService.updateBusinessFlag(businessId, flag)
          : merchantService.updateBusinessPostFundingConfig(businessId, {
              isApprovedForPostFunding: flag.isApprovedForPostFunding,
            }));
        toast.success('Business flag updated successfully');
        setTimeout(() => window.location.reload(), 1500);
      } catch (err) {
        const errorMessage = getErrorMessage(
          err,
          'An unexpected error occurred',
        );
        toast.error('Failed to update business flag: ' + errorMessage);
      } finally {
        setBusinessLoadingFlags((prev) => ({...prev, [flagKey]: false}));
      }
    },
    [businessId],
  );

  const updateBusinessPostFundingConfig = useCallback(
    async (config: PostFundingConfigPayload): Promise<void> => {
      if (!businessId) {
        return;
      }

      try {
        setIsPostFundingConfigSubmitting(true);
        await merchantService.updateBusinessPostFundingConfig(
          businessId,
          config,
        );
        toast.success('Post-funding config updated successfully');
        setTimeout(() => window.location.reload(), 1500);
      } catch (err) {
        const errorMessage = getErrorMessage(
          err,
          'An unexpected error occurred',
        );
        toast.error('Failed to update post-funding config: ' + errorMessage);
      } finally {
        setIsPostFundingConfigSubmitting(false);
      }
    },
    [businessId],
  );

  useEffect(() => {
    if (!businessId) {
      return;
    }
    // Both fetchers handle their own failures (toast / console), so these only
    // fire if that catch or the finally itself throws. Log, do not swallow.
    fetchBusinessDetails().catch(
      logRejection('Failed to fetch business details for KYB'),
    );
  }, [businessId]);

  return {
    businessDetails,
    loading,
    businessLoadingFlags,
    updateBusinessKYB,
    requestAdditionalDocuments,
    fileUrls,
    updateBusinessFlag,
    updateBusinessPostFundingConfig,
    isPostFundingConfigSubmitting,
    loadingKYB,
  };
};

export default useMerchantKYB;
